import React, { useEffect, useMemo } from 'react';
import { Helmet } from "react-helmet";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { css } from "@emotion/react";

import { getSiteTitle, MANAGE_URL } from "../../config/config";
import WizardPanel from "../components/WizardPanel";
import PlanSelection from "../components/PlanSelection";
import Auth from "./auth/Auth";
import {
    AUTH_URL,
    EMAIL_VERIFICATION_URL,
    getUrl,
    PAYMENT_URL,
    SELECT_PLAN_URL,
    SOCIAL_CONFIRMATION_URL,
    SUCCESS_URL
} from "../includes/pricingAuthRedirects";
import EmailVerification from "./auth/EmailVerification";
import SocialSignUp from "./social/SocialSignUp";
import PaymentSuccess from "../components/PaymentSuccess";
import useStripePlans from "../hooks/useStripePlans";
import Loading from "./utils/Loading";
import theme from "../../theme";
import {
    needEmailVerification,
    needSocialConfirmation,
} from "../includes/auth";
import { useAppState } from "../contexts/AppContext";

const PageRoutes = () => {
    const [appState, setAppState] = useAppState();
    const location = useLocation();
    const navigate = useNavigate();
    const { getPlans } = useStripePlans()

    useEffect(() => {
        if (appState.plans.length || !appState.hasUserBeenLoaded || !appState.isLocalisationLoaded) {
            return;
        }

        const countryCode = appState.user?.CountryCodeFromIp || appState.country.codeFromIP || "";
        setAppState({
            isLoadingPlans: true
        });
        getPlans(countryCode).then(() => {
            setAppState({
                isLoadingPlans: false
            });
        });
    }, [
        appState.plans.length,
        appState.hasUserBeenLoaded,
        appState.isLocalisationLoaded,
        appState.user?.CountryCodeFromIp,
        appState.country.codeFromIP,
        getPlans,
        setAppState
    ]);

    useEffect(() => {
        if (appState.user?.Subscription && location.pathname !== getUrl(SUCCESS_URL)) {
            window.location.href = MANAGE_URL;
        }
    }, [appState.user, location.pathname]);

    useEffect(() => {
        if (appState?.user && !appState.isChangingEmail && !appState.user?.IsEmailVerified) {
            if (location.pathname !== getUrl(SOCIAL_CONFIRMATION_URL) && needSocialConfirmation(appState?.user)) {
                navigate(getUrl(SOCIAL_CONFIRMATION_URL),
                    { state: { redirectTo: SELECT_PLAN_URL } }
                );
            } else if (location.pathname !== getUrl(EMAIL_VERIFICATION_URL) && needEmailVerification(appState?.user)) {
                if (location.pathname !== getUrl(AUTH_URL)) {
                    navigate(getUrl(EMAIL_VERIFICATION_URL));
                }
            }
        }
    }, [appState, navigate, location]);

    const wizardPanelStages = useMemo(() => {
        return [
            {
                stage: 'select-plan',
                title: 'Select a plan',
                link: getUrl(SELECT_PLAN_URL),
            },
            ...!appState.user ? [{
                stage: 'auth',
                title: 'Create account',
            }] : [],
            {
                stage: 'payment',
                title: 'Payment',
            },
        ];
    }, [appState.user]);

    const activeStage = useMemo(() => {
        switch (location.pathname) {
            case getUrl(SELECT_PLAN_URL):
                return 'select-plan';
            case getUrl(AUTH_URL):
                return 'auth';
            case getUrl(PAYMENT_URL):
                return 'payment';
            default:
                return '';
        }
    }, [location.pathname]);

    return (
        <div css={routesContainerStyle}>
            {(appState.isLoadingPlans || !appState.hasUserBeenLoaded || !appState.isLocalisationLoaded || (appState?.user?.Subscription && location.pathname !== getUrl(SUCCESS_URL))) ? (
                <Loading />
            ) : (
                <>
                    <Helmet>
                        <title>{getSiteTitle('Sign Up')}</title>
                    </Helmet>
                    {activeStage && (
                        <WizardPanel stages={wizardPanelStages} activeStage={activeStage} />
                    )}
                    <Routes>
                        <Route
                            path={getUrl(SELECT_PLAN_URL)}
                            element={
                                <PlanSelection isPayment={false} />
                            }
                        />
                        <Route
                            path={getUrl(PAYMENT_URL)}
                            element={
                                <PlanSelection isPayment={true} />
                            }
                        />
                        <Route path={getUrl(SUCCESS_URL)} element={<PaymentSuccess />} />
                        {(!appState.user || appState.isChangingEmail) && (
                            <Route path={getUrl(AUTH_URL)} element={<Auth />} />
                        )}
                        <Route path={getUrl(EMAIL_VERIFICATION_URL)} element={<EmailVerification />} />
                        {needSocialConfirmation(appState?.user) && (
                            <Route path={getUrl(SOCIAL_CONFIRMATION_URL)} element={<SocialSignUp />} />
                        )}
                        <Route
                            path="*"
                            element={
                                <Navigate to={getUrl(SELECT_PLAN_URL)} replace={true} />
                            }
                        />
                    </Routes>
                </>
            )}
        </div>
    )
}

export default PageRoutes;


const routesContainerStyle = css`
    min-height: 611px;
    padding: 0 25% 0 25%;

    ${theme.breakpoints.only('lg')} {
        padding: 0 15% 0 15%;
    }

    ${theme.breakpoints.down('sm')} {
        padding: 0 10% 0 10%;
    }
`;